import React, {
  createContext,
  ReactNode,
  SetStateAction,
  useState,
  useContext,
  useCallback,
} from 'react'

import { Button, Flex, Icon, SVG, Text } from '@clear/design-system'

type FocusedState = {
  isFocusing: boolean
  setIsFocusing: React.Dispatch<SetStateAction<boolean>>
}

type PrimaryVariant = FocusedState & {
  variant: 'primary'
  header: string
  content: string | ReactNode
  cta: string

  cancellable: boolean
  canDismiss: boolean
  onDismiss?: () => void
  onConfirm?: () => void
}

type CustomVariant = FocusedState & {
  variant: 'custom'
  header: React.FC<unknown>
  content: React.FC<unknown>
  cta: React.FC<unknown>

  cancellable: boolean
  canDismiss: boolean
  onDismiss?: undefined
  onConfirm?: undefined
}

type FocusTask = PrimaryVariant | CustomVariant

/** exported for testing purposes */
export const Modal: React.FC<PrimaryVariant | CustomVariant> = ({
  header,
  content,
  cta,

  cancellable,
  canDismiss,
  isFocusing: showModal,
  setIsFocusing: setShowModal,

  onDismiss,
  onConfirm,
}) => {
  const [isProcessing, setIsProcessing] = useState(false)

  if (!showModal) {
    return null
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const focus = (element: { focus?: () => void }) => element?.focus!()

  const dedupe = (invoke: () => Promise<void>) => async () => {
    if (isProcessing) {
      return
    }
    setIsProcessing(true)
    await invoke()
    setIsProcessing(false)
  }

  const onClose = dedupe(async () => {
    if (canDismiss) {
      await Promise.resolve(onDismiss?.())
      setShowModal(false)
    }
  })

  const onCTA = dedupe(async () => {
    await Promise.resolve(onConfirm?.())
    setShowModal(false)
  })

  const CloseButton: React.FC = () => {
    if (!cancellable) {
      return null
    }
    /**
     * ! SVG are not keyboard focusable we should replace with an image button
     * * note: ported from shared modal
     */
    return (
      <button ref={focus as never} className="relative" onClick={onClose}>
        <SVG
          name="x-circle"
          color="neutralsTextGray"
          size="22"
          className="cursor-pointer absolute -top-5 -right-5"
        />
      </button>
    )
  }

  const Header: React.FC =
    typeof header === 'string'
      ? () => (
          <Flex
            data-testid="modal-header"
            flexDirection="row"
            alignItems="center"
            sx={{ gap: '0.5rem' }}
            mb="1rem"
          >
            <Icon name="alert-triangle" color={'#DB3341' as never} />
            <Text as="h3" variant="medium" fontSize="18px" textAlign="left">
              {header}
            </Text>
          </Flex>
        )
      : header

  const Content: React.FC =
    typeof content !== 'function'
      ? () => <Text data-testid="modal-content">{content}</Text>
      : (content as React.FC)

  const Footer =
    typeof cta === 'string'
      ? () => (
          <Button
            ref={(!cancellable ? focus : null) as never}
            data-testid="modal-primary-cta"
            text={cta}
            variant="secondary"
            sx={{
              '& span': {
                color: '#041A55',
              },
            }}
            onClick={onCTA}
            px="4rem"
            py="1rem"
            mt="1.5rem"
            mx="8.625rem"
            disabled={isProcessing}
          />
        )
      : cta

  return (
    <>
      <div
        data-testid="modal-shroud"
        className="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none"
        onClick={onClose}
      >
        <Flex
          p="2rem"
          flexDirection="column"
          backgroundColor="neutralsWhite"
          width={['375px', '534px']}
          borderRadius="8px"
          onClick={(e) => e.stopPropagation()}
          mx={[6, null]}
        >
          <CloseButton />
          <Flex flexDirection="column">
            <Header />
            <Content />
            <Footer />
          </Flex>
        </Flex>
      </div>
      <div className="fixed inset-0 z-40 bg-black opacity-25" />
    </>
  )
}

/**
 * exported for testing purposes
 *
 * TODO: confirm w/ design for best UX practices; plan for later
 * * - render sheets on mobile
 * * - render modals on desktop
 */
export const FocusTask: React.FC<FocusTask> = ({
  children,

  ...props
}) => {
  return (
    <>
      {children}
      <Modal {...props} />
    </>
  )
}

export type FocusTaskState = Pick<
  PrimaryVariant,
  'isFocusing' | 'header' | 'content' | 'cta' | 'onDismiss' | 'onConfirm'
> &
  Partial<Pick<PrimaryVariant, 'canDismiss'>>

const FocusTaskContext = createContext<{
  focusTask: FocusTaskState
  setFocusTask: React.Dispatch<React.SetStateAction<FocusTaskState>>
}>(
  /**
   * * note: `never` is intentional
   * - bricks when context-hook is accessed outside of provider's initialization
   * - typing also allows us to avoid optional handling when accessed by children
   */
  undefined as never
)

export const FocusTaskProvider = ({
  children,
}: Readonly<{
  children: React.ReactNode
}>) => {
  const [focusTask, setFocusTask] = useState<FocusTaskState>({
    isFocusing: false,
    header: 'Payment not processed (test)',
    content: (
      <>
        There was an issue with <b>Visa *1234</b>. Please try again or contact
        your card provider.
      </>
    ),
    cta: 'Okay',
  })

  const setIsFocusing = useCallback(
    (isFocusing: boolean) =>
      setFocusTask((state) => ({
        ...state,
        isFocusing,
      })),
    []
  )

  const { canDismiss = true, ...task } = focusTask
  return (
    <FocusTaskContext.Provider value={{ focusTask, setFocusTask }}>
      <FocusTask
        variant="primary"
        cancellable={false}
        setIsFocusing={setIsFocusing}
        canDismiss={canDismiss}
        {...task}
      >
        {children}
      </FocusTask>
    </FocusTaskContext.Provider>
  )
}

export function useFocusContext() {
  const context = useContext(FocusTaskContext)
  if (context === undefined) {
    throw new Error('useFocusContext must be used within an FocusProvider')
  }
  return context
}
